/**
 * The AMI benchmark line, attached to a finished chart model (HOF-19 Phase B).
 *
 * **Attach, not participate.** `buildAggregateChartModel` computes everything a
 * farmer negotiates with — the mengengewichteter Durchschnitt, the day panel,
 * the forecasts, the bridges — and AC 8 forbids the benchmark from moving any
 * of it. So this layer runs AFTER the build, takes the model as read-only
 * input, and only ever ADDS: rows for dates the farm did not trade, and line
 * specs the legend and plot pick up through the shapes they already render.
 * The own model's structures pass through untouched (pinned byte-for-byte in
 * the tests), which is the client-side mirror of the server publishing the
 * benchmark under a fact type no own-price reader names.
 *
 * The row union is safe by the chart's own mechanics: several lines already
 * share one row set over the union of their dates, each null on dates owned by
 * another, and segments render with `connectNulls` (#2488). The ONE seam the
 * union genuinely crosses is the dashed forecast, which draws with
 * `connectNulls={false}` — an inserted row inside its span would snap it. The
 * carried price is flat by construction, so this layer copies the neighbouring
 * value onto inserted rows: exact, not an interpolation.
 *
 * **What the benchmark line is not given, deliberately:**
 * - no forecast and no "Heute" price label — a reference is compared against,
 *   not held or projected;
 * - no carry-forward bridges — a bridge claims nothing happened in a gap, and
 *   this layer cannot certify the panel's gaps;
 * - no panel column — the drill-down answers "what is this point made of", and
 *   a benchmark point is made of retail observations the own-price panel
 *   cannot show (its own drill-down is a later, separate read).
 *
 * Greyscale first (AC 6): the line is told apart by DASH, not by hue — dotted,
 * against the solid own lines and the long-dashed Kauf side — and thinner than
 * the live lines, because a reference is background.
 */
import {
  BENCHMARK_LEVEL_STATISTIC,
  benchmarkLevel,
  benchmarkSourceLabel,
  type BenchmarkLevel,
  type BenchmarkStatistic,
} from './benchmarkCells';
import { type Quality } from './catalog';
import {
  GAP_BREAK_DAYS,
  type AggChartModel,
  type AggChartRow,
  type AggLineSpec,
  type BenchmarkBucketFacts,
} from './aggregateChartModel';
import { bucketStartEpochDay, epochDayToIso, type Grain } from './grain';
import { toEpochDay } from './priceAt';
import type { PriceSeries } from './types';

/** Namespaces every benchmark line id, so nothing can collide with a GroupKey. */
export const BENCHMARK_LINE_PREFIX = 'bm__';

/**
 * One dash per market LEVEL — distinguishable from the solid own lines, from
 * the `SIDE_DASH` ('7 3') Kauf dash, and from EACH OTHER by pattern alone, on
 * a greyscale print and to a colour-blind reader (AC 6).
 *
 * Two dashes rather than one because there are now two reference lines. Seen
 * on the local stack: a 4½-year Großmarkt line and a one-week consumer line
 * rendered in the identical grey `2 4`, and nothing but the legend chip could
 * say which was which — on a chart whose entire point is that the two are
 * prices at opposite ends of the chain.
 *
 * Consumer keeps the established fine dot; trade takes a longer dash-gap that
 * reads as a different rhythm at every zoom and cannot be confused with the
 * Kauf side's heavier '7 3'.
 *
 * The dash SURVIVES the move to colour (below) rather than being replaced by
 * it: it is what carries the distinction on a greyscale print, and it is the
 * secondary encoding that keeps the pair legible to a colour-blind reader.
 */
export const BENCHMARK_DASH: Record<BenchmarkLevel, string> = {
  consumer: '2 4',
  trade: '5 3 1 3',
};

/**
 * One muted hue per level (Antonio, 2026-09-02).
 *
 * This layer used to draw both lines in one neutral grey and argue that hue was
 * unavailable — that colour would promote the benchmark over the farm's own
 * price and fail the greyscale reader. The first half was right about
 * SATURATION, not about hue, and the second is answered by keeping the dashes:
 * two dashes at 1.5 px in the same grey are not actually distinguishable on a
 * real chart, which is the whole complaint. A distinction the reader cannot
 * make is not a conservative choice.
 *
 * So: two hues, both desaturated well below the own lines' teal `#0f766e`
 * (Verkauf) and amber `#b45309` (Kauf), and clear of the channel palette's
 * violet `#7c5cff`, pink `#e0568a` and teal `#0f9d8f`. Blue against ochre is a
 * CVD-safe pair — it separates on the one axis both protan and deutan vision
 * keeps — and neither reads as a status colour.
 *
 * Blue for the consumer end and ochre for the trade end, not the reverse:
 * ochre sits nearer the earth-toned end and the trade line is the one a farmer
 * compares his own delivery price against, so it belongs beside the own lines
 * rather than opposite them.
 */
export const BENCHMARK_LEVEL_COLOR: Record<BenchmarkLevel, string> = {
  consumer: '#4a6fa5',
  trade: '#96703c',
};

/** Thinner than a live line — background, never the protagonist. */
export const BENCHMARK_STROKE_PX = 1.5;

const DAY_MS = 86_400_000;

export interface AttachBenchmarkOptions {
  grain: Grain;
  today: string;
  /** The chart's quality selection, applied exactly as to an own series. */
  quality?: Quality;
  hiddenLineIds?: readonly string[];
}

interface BenchmarkBucket extends BenchmarkBucketFacts {
  e: number;
  value: number;
  /** The chain was re-anchored at or inside this bucket — see {@link splitRuns}. */
  chainBreak: boolean;
}

function bucketsFor(
  series: PriceSeries,
  grain: Grain,
  loE: number,
  hiE: number,
): BenchmarkBucket[] {
  // Per bucket, the statistic the LEVEL claims (`BENCHMARK_LEVEL_STATISTIC`).
  //
  // A shelf price has no traded quantity behind it, so neither level is ever
  // weighted by volume. But a `mean` level's weekly point IS an average of a
  // countable number of observations, and the drill-down asks the server for
  // the DISPLAY grain — so in month view the panel lists every notation of the
  // month and heads them `Ø`. Averaging the weekly means unweighted equals that
  // only when every week carried the same count: 17 of the 159 German-origin
  // Erdbeeren weeks have a single reporting market, and one of those beside
  // three four-market weeks would be weighted 1/4 instead of 1/13. Weight by
  // the point's own `n` and the invariant survives the re-bucket. Found by
  // codex on this change.
  //
  // An `index` level stays unweighted: its weekly point is an estimate of a
  // LEVEL, not a sum of observations, so there is nothing to weight it by —
  // and its tile deliberately carries no `Ø`, so no arithmetic is promised.
  //
  // HOF-250 makes the BUCKET state its own statistic, because a consumer line
  // now carries both: a bucket is a `mean` only where every point in it is one
  // AND the arithmetic that combined them was the mean's own. On a weighted
  // level that is always true. Off it, only a bucket holding a SINGLE mean
  // point qualifies — the consumer level's `n` counts relatives, not
  // observations, so there is no honest weight to average two of its weekly
  // means by, and an unweighted average of means is not the mean of the rows
  // the month's drill-down lists. That bucket keeps the level's `index`: it
  // under-claims, which is the only direction this may ever fail in.
  const level = benchmarkLevel(series.seriesId);
  const fallback = level != null ? BENCHMARK_LEVEL_STATISTIC[level] : 'index';
  const weighted = fallback === 'mean';
  const sums = new Map<
    number,
    {
      sum: number;
      w: number;
      plain: number;
      n: number;
      points: number;
      means: number;
      ranged: number;
      min: number;
      max: number;
      broke: boolean;
    }
  >();
  for (const point of series.points) {
    const e = bucketStartEpochDay(toEpochDay(point.date), grain);
    if (e < loE || e > hiE) continue;
    const acc =
      sums.get(e) ??
      { sum: 0, w: 0, plain: 0, n: 0, points: 0, means: 0, ranged: 0, min: Infinity, max: -Infinity, broke: false };
    // A point with no count still counts as one observation — the same
    // defensive rule the server's own weighted mean uses, so a payload that
    // lost its counts degrades to the plain mean rather than to a division by
    // zero.
    const w = weighted ? point.lineCount || 1 : 1;
    acc.sum += point.value * w;
    acc.w += w;
    // The unweighted sum, kept beside the weighted one (HOF-544, codex r4): the
    // count weight is honest only where EVERY point in the bucket is a mean. A
    // trade week folded with the Shared Buy Index is a fixed-weight composite,
    // and its `n` is not a weight anyone chose — so such a bucket averages its
    // weeks plainly, and says `index` rather than `Ø`.
    acc.plain += point.value;
    // HOF-269 §2 — what the points DECLARE they stand for, carried through the
    // re-bucket so a card can state it. Kept apart from the weight `w` above,
    // which is 1 per point on an unweighted level and would silently become a
    // POINT count in a sentence that says "Beobachtungen". What the number
    // means still varies by level (observations on a `mean` level, relatives on
    // a chained one — `benchmark_index._chain_one_year` says so in as many
    // words), which is why the reader of this field gates on `statistic`.
    acc.n += point.lineCount || 1;
    acc.points += 1;
    if ((point.statistic ?? fallback) === 'mean') acc.means += 1;
    // The observed range (#2671), and ONLY where every point in the bucket
    // states one. A range folded over some points and not others would be
    // narrower than the truth while looking like the whole span — and a bucket
    // that states none is the normal case today, because no benchmark cell on
    // the wire carries `min`/`max` yet. Absent degrades to "no Spanne clause",
    // which is silence rather than a wrong number.
    if (typeof point.obsMin === 'number' && typeof point.obsMax === 'number') {
      acc.ranged += 1;
      if (point.obsMin < acc.min) acc.min = point.obsMin;
      if (point.obsMax > acc.max) acc.max = point.obsMax;
    }
    // ANY re-anchor in the bucket breaks it off its predecessor. At the bucket's
    // first point the previous bucket is plainly on the other scale; at a later
    // one the bucket itself straddles both, which is no more connectable.
    if (point.chainBreak) acc.broke = true;
    sums.set(e, acc);
  }
  return [...sums.entries()]
    .map(([e, { sum, w, plain, n, points, means, ranged, min, max, broke }]) => ({
      e,
      value: weighted && means === points ? sum / w : plain / points,
      n,
      statistic: (means === points && (weighted || points === 1) ? 'mean' : 'index') as BenchmarkStatistic,
      ...(ranged === points ? { min, max } : {}),
      chainBreak: broke,
    }))
    .sort((a, b) => a.e - b.e);
}

/**
 * The drawn runs — split at a seasonal GAP, and at a chain BREAK.
 *
 * The gap rule is the own lines' (`GAP_BREAK_DAYS`): a winter with no panel
 * observations is a structural break, not a line to draw across.
 *
 * The break rule is the index's own (HOF-250 rev. 3, codex P1). Where the
 * server re-anchored — a week that formed no relatives, so nothing connected it
 * to its predecessor — the two sides are two anchors on two scales, and the
 * step between them is not a movement anybody measured. Drawing through it says
 * exactly what the tile's withheld `Ø` refuses to say: the 7,48 → 10,37 pair
 * this revision exists for is Kaufland's Angebot beside Rewe's Regal, and a
 * continuous line reads it as a +39 % week. The values are right either way;
 * what breaks is the claim the line makes BETWEEN them.
 */
function splitRuns(buckets: readonly BenchmarkBucket[]): { fromE: number; toE: number }[] {
  if (buckets.length === 0) return [];
  const runs: { fromE: number; toE: number }[] = [];
  let fromE = buckets[0].e;
  let prev = buckets[0].e;
  for (let i = 1; i < buckets.length; i += 1) {
    const { e, chainBreak } = buckets[i];
    if (e - prev > GAP_BREAK_DAYS || chainBreak) {
      runs.push({ fromE, toE: prev });
      fromE = e;
    }
    prev = e;
  }
  runs.push({ fromE, toE: prev });
  return runs;
}

function benchmarkLabel(series: PriceSeries): string {
  // Named per POPULATION, not per layer: since the Public Sell Index there are
  // two benchmark lines, and one shared name would leave the reader unable to
  // tell a licensed panel's shop visits from an open scrape of shelf prices.
  return benchmarkSourceLabel(series.seriesId);
}

/**
 * The chart model with the AMI benchmark attached, or the INPUT MODEL ITSELF
 * when there is nothing to attach — referential identity, so absence of
 * benchmark data renders the pre-HOF-19 chart down to the object (AC 14).
 */
export function attachBenchmarkLayer(
  chart: AggChartModel,
  benchmarkSeries: readonly PriceSeries[],
  opts: AttachBenchmarkOptions,
): AggChartModel {
  const { grain, today, quality, hiddenLineIds = [] } = opts;
  const hidden = new Set(hiddenLineIds);

  const windowLoE = Math.round(chart.domain.lo / DAY_MS);
  const windowHiE = Math.round(chart.domain.hi / DAY_MS);
  // Observations only, capped at today like a live line: the panel records
  // shelf visits that happened, and nothing about the future.
  const upperE = Math.min(toEpochDay(today), windowHiE);

  // A quality-STAMPED series follows the chart's quality selection exactly as
  // an own series does. The MERGED matched-model index series (HOF-214) carries
  // no quality — it pools bio and konventionell, with `organic` a cell-key
  // dimension inside the index — so it represents both and survives every
  // selection: filtering it away would silently remove the AMI line the moment
  // a reader narrows to Bio, with the selector looking like the cause.
  const selected = benchmarkSeries.filter(
    (s) => s.layer === 'benchmark' && (quality == null || s.quality == null || s.quality === quality),
  );

  const plans = selected
    .map((series) => {
      const id = `${BENCHMARK_LINE_PREFIX}${series.seriesId}`;
      return { series, id, buckets: bucketsFor(series, grain, windowLoE, upperE), hidden: hidden.has(id) };
    })
    .filter((p) => p.buckets.length > 0);

  if (plans.length === 0) return chart;

  // ── Rows: union of the own dates and the benchmark bucket dates ───────────
  const byDate = new Map<string, AggChartRow>(chart.rows.map((row) => [row.date, { ...row }]));
  const insertedDates: string[] = [];
  for (const plan of plans) {
    for (const bucket of plan.buckets) {
      const date = epochDayToIso(bucket.e);
      if (!byDate.has(date)) {
        byDate.set(date, { date, ts: bucket.e * DAY_MS });
        insertedDates.push(date);
      }
    }
  }

  const rows = [...byDate.values()].sort((a, b) => a.ts - b.ts);

  // The forecast repair — see the module header. Only the fc keys need it: the
  // segment and bridge lines render with `connectNulls`, so an inserted row
  // they carry no value on is invisible to them.
  if (insertedDates.length > 0) {
    const fcKeys = chart.lines.filter((l) => l.fcKey !== '').map((l) => l.fcKey);
    const inserted = new Set(insertedDates);
    for (const key of fcKeys) {
      for (let i = 1; i < rows.length - 1; i += 1) {
        if (!inserted.has(rows[i].date)) continue;
        // Walk to the nearest ORIGINAL neighbours: two inserted rows can sit
        // side by side, and an inserted one holds no value to copy.
        let prev = i - 1;
        while (prev > 0 && inserted.has(rows[prev].date)) prev -= 1;
        let next = i + 1;
        while (next < rows.length - 1 && inserted.has(rows[next].date)) next += 1;
        const before = rows[prev][key];
        const after = rows[next][key];
        // Inside the span iff both original neighbours carry the value; the
        // carried price is flat, so the copy is exact.
        if (typeof before === 'number' && typeof after === 'number') {
          rows[i][key] = before;
        }
      }
    }
  }

  // ── The benchmark values and line specs ────────────────────────────────────
  let yLo = chart.yExtent?.lo ?? Infinity;
  let yHi = chart.yExtent?.hi ?? -Infinity;

  const specs: AggLineSpec[] = plans.map((plan) => {
    const valueByDate = new Map(plan.buckets.map((b) => [epochDayToIso(b.e), b.value]));
    const runs = splitRuns(plan.buckets);
    const segments = runs.map((run, i) => ({
      key: `${plan.id}_s${i}`,
      fromTs: run.fromE * DAY_MS,
      toTs: run.toE * DAY_MS,
    }));

    for (const row of rows) {
      const value = valueByDate.get(row.date) ?? null;
      for (const seg of segments) {
        const inSegment = value != null && row.ts >= seg.fromTs && row.ts <= seg.toTs;
        row[seg.key] = inSegment ? value : null;
        // A hidden line must not drag the scale from off-screen — #2590's rule,
        // applied to the layer the same way the model applies it.
        if (inSegment && !plan.hidden && value != null) {
          if (value < yLo) yLo = value;
          if (value > yHi) yHi = value;
        }
      }
    }

    return {
      id: plan.id,
      // Never a real GroupKey, so the panel cannot resolve a column through it.
      groupKey: plan.id,
      // The panel records selling prices; 'verkauf' is that fact seen from the
      // shop counter. Required by the shared spec, read only by the legend
      // grouping — the benchmark adds no panel column either way.
      side: 'verkauf',
      channel: 'all',
      offset: 0,
      segments,
      bridges: [],
      fcKey: '',
      todayValue: null,
      labelSlot: 0,
      label: benchmarkLabel(plan.series),
      color: BENCHMARK_LEVEL_COLOR[benchmarkLevel(plan.series.seriesId) ?? 'consumer'],
      dash: BENCHMARK_DASH[benchmarkLevel(plan.series.seriesId) ?? 'consumer'],
      strokeWidth: BENCHMARK_STROKE_PX,
      hidden: plan.hidden,
      benchmarkBucketByDate: Object.fromEntries(
        plan.buckets.map((b) => [
          epochDayToIso(b.e),
          {
            statistic: b.statistic,
            n: b.n,
            ...(b.min !== undefined ? { min: b.min } : {}),
            ...(b.max !== undefined ? { max: b.max } : {}),
          },
        ]),
      ),
    };
  });

  const drawnSpecs = specs.filter((s) => !s.hidden);

  // Z-order is recharts source order: overlays under everything, then the
  // benchmark, then the live own lines — the market is context, and the farm's
  // own price must never be occluded by it. Legend order is `lines` order, and
  // there the benchmark reads LAST, after the lines the page is about.
  const firstLive = chart.drawn.findIndex((l) => l.offset === 0);
  const drawn =
    firstLive === -1
      ? [...chart.drawn, ...drawnSpecs]
      : [...chart.drawn.slice(0, firstLive), ...drawnSpecs, ...chart.drawn.slice(firstLive)];

  return {
    ...chart,
    rows,
    lines: [...chart.lines, ...specs],
    drawn,
    yExtent: yLo === Infinity ? chart.yExtent : { lo: yLo, hi: yHi },
    // The overlay rule, one layer over: a chart whose only data is a drawn
    // benchmark line must not render "Keine Daten" on top of it. `hasData`
    // stays the OWN answer — it governs the drill-down, which stays own-only.
    hasPlot: chart.hasPlot || drawnSpecs.some((s) => s.segments.length > 0),
  };
}
